import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronUp,
  Copy,
  Download,
  Info,
  RefreshCw,
  Search,
  XCircle
} from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

function AdminWithdraw() {
  const [requests, setRequests] = useState([]);
  const [utr, setUtr] = useState({});
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await API.get("/admin/withdraw-requests");
      setRequests(res.data);
    } catch (err) {
      console.log(err);
      alert("Error loading data");
    }
  };

  const approve = async (request) => {
    try {
      await API.post("/admin/approve-withdraw", {
        request_id: request.id,
        utr: utr[request.id] || ""
      });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to approve withdrawal");
    }
  };

  const hold = async (id) => {
    try {
      await API.post("/admin/hold-withdraw", { id });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to hold withdrawal");
    }
  };

  const unhold = async (id) => {
    try {
      await API.post("/admin/unhold-withdraw", { id });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to unhold withdrawal");
    }
  };

  const reject = async (id) => {
    try {
      await API.post("/admin/reject-withdraw", { id });
      loadData();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to reject withdrawal");
    }
  };

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return requests.filter((request) => {
      const status = String(request.status || "pending").toLowerCase();
      const matchesFilter = filter === "all" || status === filter;
      const matchesSearch = !search || [
        request.id,
        request.user_name,
        request.user_phone,
        request.agent_name,
        request.amount,
        request.utr,
        request.bank_name,
        request.account_number,
        request.upi_id
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
      const matchesDate = !selectedDate || (request.created_at || "").slice(0, 10) === selectedDate;

      return matchesFilter && matchesSearch && matchesDate;
    });
  }, [filter, query, requests, selectedDate]);

  const summaryRows = useMemo(() => {
    return requests.filter((request) => !selectedDate || (request.created_at || "").slice(0, 10) === selectedDate);
  }, [requests, selectedDate]);

  const pendingCount = countByStatus(summaryRows, "pending");
  const holdCount = countByStatus(summaryRows, "hold");
  const approvedCount = countByStatus(summaryRows, "approved");
  const rejectedCount = countByStatus(summaryRows, "rejected");

  const copyRequestDetails = async (request) => {
    const text = [
      `Request ID: ${request.id}`,
      `Date: ${formatDateTime(request.created_at)}`,
      `User: ${request.user_name || "User"}`,
      `Mobile: ${request.user_phone || "-"}`,
      request.agent_name ? `Agent: ${request.agent_name}` : "",
      `Amount: ${formatMoney(request.amount)}`,
      ...getPaymentLines(request),
      `Status: ${String(request.status || "").toUpperCase()}`,
      request.utr ? `UTR: ${request.utr}` : ""
    ].filter(Boolean).join("\n");

    try {
      await navigator.clipboard.writeText(text);
      alert("User payout details copied");
    } catch {
      alert("Unable to copy details");
    }
  };

  const exportCsv = () => {
    downloadCsv("payouts.csv", [
      ["Request ID", "Date", "User", "Mobile", "Amount", "Bank", "Account Holder", "Account Number", "IFSC", "UPI", "UTR", "Status"],
      ...filtered.map((request) => [
        request.id,
        formatDateTime(request.created_at),
        request.user_name || "User",
        request.user_phone || "",
        Number(request.amount || 0),
        request.bank_name || "",
        request.account_holder || "",
        request.account_number || "",
        request.ifsc || "",
        request.upi_id || "",
        request.utr || "",
        request.status || ""
      ])
    ]);
  };

  return (
    <AdminLayout>
      <section style={page}>
        <header style={pageHeader}>
          <div>
            <h1 style={title}>Payout Management</h1>
            <p style={subtitle}>Manage withdrawal requests</p>
          </div>

          <div style={headerActions}>
            <button type="button" style={iconButton} onClick={loadData}><RefreshCw size={14} /></button>
            <button type="button" style={exportButton} onClick={exportCsv}><Download size={14} /> Export</button>
          </div>
        </header>

        <section style={summaryPanel}>
          <div style={summaryTitle}>
            <span style={summaryIcon}><Info size={15} /></span>
            <span>
              <strong>Payout Summary</strong>
              <small>{selectedDate ? `For ${selectedDate}` : "All dates"}</small>
            </span>
            <ChevronUp size={14} />
          </div>

          <div style={summaryGrid}>
            <SummaryCard label="Pending" value={pendingCount} foot={formatMoney(sumByStatus(summaryRows, "pending"))} color="#fde68a" bg="rgba(245,158,11,0.25)" onClick={() => setFilter("pending")} />
            <SummaryCard label="Hold" value={holdCount} foot={formatMoney(sumByStatus(summaryRows, "hold"))} color="#7dd3fc" bg="rgba(14,165,233,0.2)" onClick={() => setFilter("hold")} />
            <SummaryCard label="Approved" value={approvedCount} foot={formatMoney(sumByStatus(summaryRows, "approved"))} color="#6ee7b7" bg="rgba(16,185,129,0.2)" onClick={() => setFilter("approved")} />
            <SummaryCard label="Rejected" value={rejectedCount} foot={formatMoney(sumByStatus(summaryRows, "rejected"))} color="#fca5a5" bg="rgba(239,68,68,0.2)" onClick={() => setFilter("rejected")} />
            <SummaryCard label="Total Requests" value={summaryRows.length} foot={formatMoney(sumAll(summaryRows))} color="#c7d2fe" bg="rgba(99,102,241,0.25)" onClick={() => setFilter("all")} />
          </div>
        </section>

        <div style={toolbar}>
          <div style={searchBox}>
            <Search size={14} color="#94a3b8" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search user, ID, mobile..."
              style={searchInput}
            />
          </div>

          <label style={datePicker}>
            <CalendarDays size={13} />
            <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} style={dateInput} />
          </label>
          {selectedDate && (
            <button type="button" style={clearDateButton} onClick={() => setSelectedDate("")}>All dates</button>
          )}

          {[
            ["pending", "Pending Requests", pendingCount],
            ["hold", "Hold", holdCount],
            ["approved", "Approved", approvedCount],
            ["rejected", "Rejected", rejectedCount],
            ["all", "All", requests.length]
          ].map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              style={filter === key ? activeFilter : filterButton}
              onClick={() => setFilter(key)}
            >
              {label} {key === "pending" && <span style={countDot}>{count}</span>}
            </button>
          ))}
        </div>

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Request ID</th>
                <th style={th}>User</th>
                <th style={th}>Amount</th>
                <th style={th}>Payment Details</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((request) => (
                <tr key={request.id} style={tr}>
                  <td style={td}>
                    <strong>{request.id}</strong>
                    <small>{formatDateTime(request.created_at)}</small>
                  </td>
                  <td style={td}>
                    <div style={userCell}>
                      <span style={avatar}>{getInitial(request.user_name)}</span>
                      <div>
                        <strong style={userName}>{request.user_name || "User"}</strong>
                        <div style={userMeta}>
                          <span>{request.user_phone || "-"}</span>
                          {request.agent_name && <span style={seriesMeta}>Agent: {request.agent_name}</span>}
                          {(request.series || request.series_number) && (
                            <span style={seriesMeta}>Series: {request.series || request.series_number}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={td}><strong>{formatMoney(request.amount)}</strong></td>
                  <td style={td}>
                    <PaymentDetails request={request} />
                  </td>
                  <td style={td}><StatusBadge status={request.status} /></td>
                  <td style={td}>
                    <div style={actions}>
                      {request.status === "pending" ? (
                        <>
                          <input
                            style={utrInput}
                            placeholder="Enter UTR"
                            value={utr[request.id] || ""}
                            onChange={(event) => setUtr({ ...utr, [request.id]: event.target.value })}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                approve(request);
                              }
                            }}
                          />
                          <button type="button" style={approveButton} onClick={() => approve(request)}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button type="button" style={holdButton} onClick={() => hold(request.id)}>
                            Hold
                          </button>
                          <button type="button" style={rejectButton} onClick={() => reject(request.id)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      ) : request.status === "hold" ? (
                        <>
                          <input
                            style={utrInput}
                            placeholder="Enter UTR"
                            value={utr[request.id] || ""}
                            onChange={(event) => setUtr({ ...utr, [request.id]: event.target.value })}
                          />
                          <button type="button" style={approveButton} onClick={() => approve(request)}>
                            <CheckCircle2 size={14} /> Approve
                          </button>
                          <button type="button" style={unholdButton} onClick={() => unhold(request.id)}>
                            Unhold
                          </button>
                          <button type="button" style={rejectButton} onClick={() => reject(request.id)}>
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      ) : (
                        <div style={actionInfoRow}>
                          <button type="button" style={copyButton} onClick={() => copyRequestDetails(request)}>
                            <Copy size={14} /> Copy
                          </button>
                          <button type="button" style={infoButton}><Info size={14} /></button>
                        </div>
                      )}
                      {(request.status === "pending" || request.status === "hold") && (
                        <button type="button" style={copyButton} onClick={() => copyRequestDetails(request)}>
                          <Copy size={14} /> Copy
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td style={emptyCell} colSpan="6">No payout requests found</td>
                </tr>
              )}
            </tbody>
          </table>

          <footer style={tableFooter}>
            <span>1-{filtered.length} of {filtered.length}</span>
            <span style={pager}>‹ <b>1</b> ›</span>
          </footer>
        </div>
      </section>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, foot, color, bg, onClick }) {
  return (
    <button type="button" style={{ ...summaryCard, background: bg, borderColor: color }} onClick={onClick}>
      <span style={{ color }}>{label}</span>
      <strong>{value}</strong>
      <small style={{ color }}>{foot}</small>
    </button>
  );
}

function PaymentDetails({ request }) {
  const lines = getPaymentLines(request);

  return (
    <div style={paymentBox}>
      {request.utr && (
        <span style={utrBadge}>
          UTR: {request.utr}
          <Copy size={11} />
        </span>
      )}
      {(request.series || request.series_number) && (
        <small style={seriesLine}>Series: {request.series || request.series_number}</small>
      )}
      {lines.length > 0 ? lines.map((line) => <small key={line}>{line}</small>) : <small>No bank details</small>}
      {request.status === "approved" && <small>By: {request.approved_by_name || "Admin"} • {formatDateTime(request.updated_at || request.created_at)}</small>}
      {request.status === "hold" && request.approved_by_name ? <small>Hold by: {request.approved_by_name}</small> : null}
      {request.status === "rejected" && request.approved_by_name ? <small>Rejected by: {request.approved_by_name}</small> : null}
    </div>
  );
}

function getPaymentLines(request) {
  return request.upi_id
    ? [`UPI: ${request.upi_id}`]
    : [
        request.bank_name && `Bank: ${request.bank_name}`,
        request.account_holder && `Name: ${request.account_holder}`,
        request.account_number && `A/C: ${request.account_number}`,
        request.ifsc && `IFSC: ${request.ifsc}`
      ].filter(Boolean);
}

function StatusBadge({ status }) {
  const styles = {
    pending: pendingBadge,
    hold: holdBadge,
    approved: approvedBadge,
    rejected: rejectedBadge
  };
  const labels = {
    pending: "Pending",
    hold: "Hold",
    approved: "Approved",
    rejected: "Rejected"
  };

  return <span style={styles[status] || pendingBadge}><CheckCircle2 size={12} /> {labels[status] || status}</span>;
}

function sumByStatus(requests, status) {
  return requests
    .filter((request) => String(request.status || "").toLowerCase() === status)
    .reduce((sum, request) => sum + Number(request.amount || 0), 0);
}

function sumAll(requests) {
  return requests.reduce((sum, request) => sum + Number(request.amount || 0), 0);
}

function countByStatus(requests, status) {
  return requests.filter((request) => String(request.status || "").toLowerCase() === status).length;
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function formatDateTime(value) {
  if (!value) return "23 Apr 26, 02:39 pm";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "23 Apr 26, 02:39 pm";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getInitial(name) {
  return (name || "U").trim().charAt(0).toUpperCase();
}

const page = { color: "#111827" };
const pageHeader = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: 13 };
const headerActions = { display: "flex", gap: 6 };
const iconButton = { width: 32, height: 32, display: "grid", placeItems: "center", border: "1px solid #e5e7eb", background: "#fff", color: "#6366f1", cursor: "pointer", borderRadius: 8 };
const exportButton = { height: 32, padding: "0 16px", display: "flex", alignItems: "center", gap: 7, border: 0, background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: 8 };
const summaryPanel = { background: "linear-gradient(135deg, #1e1b4b, #312e81)", borderRadius: 16, padding: 16, marginBottom: 16, color: "#fff" };
const summaryTitle = { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 };
const summaryIcon = { width: 32, height: 32, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.15)", borderRadius: 8 };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 };
const summaryCard = { minHeight: 70, border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4, textAlign: "left", cursor: "pointer", color: "#fff", background: "rgba(255,255,255,0.08)" };
const toolbar = { display: "flex", gap: 6, marginBottom: 12, alignItems: "center", flexWrap: "wrap" };
const searchBox = { height: 38, display: "flex", alignItems: "center", gap: 8, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", flex: 1, maxWidth: 300 };
const searchInput = { border: 0, outline: "none", flex: 1, fontSize: 13 };
const datePicker = { height: 38, display: "flex", alignItems: "center", gap: 6, border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "0 12px", color: "#64748b", fontSize: 12, cursor: "pointer" };
const dateInput = { border: 0, outline: "none", fontSize: 12, color: "#0f172a", cursor: "pointer" };
const clearDateButton = { height: 38, padding: "0 12px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 12, fontSize: 12, cursor: "pointer", color: "#64748b" };
const filterButton = { height: 34, padding: "0 14px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", gap: 6 };
const activeFilter = { ...filterButton, background: "#1e1b4b", color: "#fff", border: "1.5px solid #1e1b4b" };
const countDot = { background: "#f97316", color: "#fff", borderRadius: 999, padding: "1px 6px", fontSize: 10, fontWeight: 800 };
const tableWrap = { background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14, overflow: "auto" };
const table = { width: "100%", minWidth: "1050px", borderCollapse: "collapse" };
const th = { padding: "12px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" };
const tr = { borderBottom: "1px solid #f1f5f9" };
const td = { padding: "12px 14px", fontSize: 13, color: "#334155", verticalAlign: "middle" };
const userCell = { display: "flex", alignItems: "center", gap: 10 };
const userName = { display: "block", fontSize: 13, fontWeight: 700, color: "#0f172a" };
const userMeta = { display: "flex", flexDirection: "column", gap: 3, marginTop: 2, color: "#94a3b8", fontSize: 11 };
const seriesMeta = { color: "#374151", fontWeight: 700, fontSize: 11 };
const avatar = { width: 32, height: 32, display: "grid", placeItems: "center", borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #7c3aed)", color: "#fff", fontWeight: 800, fontSize: 13, flexShrink: 0 };
const paymentBox = { display: "grid", gap: 3, color: "#64748b", fontSize: 12 };
const utrBadge = { display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", background: "#dbeafe", color: "#1d4ed8", fontWeight: 700, fontSize: 11, borderRadius: 5, width: "fit-content" };
const seriesLine = { color: "#374151", fontWeight: 700, fontSize: 12 };
const approvedBadge = { display: "inline-flex", alignItems: "center", gap: 4, background: "#dcfce7", color: "#16a34a", padding: "4px 10px", borderRadius: 999, fontWeight: 700, fontSize: 11 };
const pendingBadge = { ...approvedBadge, background: "#fef3c7", color: "#d97706" };
const holdBadge = { ...approvedBadge, background: "#e0f2fe", color: "#0369a1" };
const rejectedBadge = { ...approvedBadge, background: "#fee2e2", color: "#dc2626" };
const actions = { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 5 };
const utrInput = { width: 110, height: 30, border: "1.5px solid #d1d5db", borderRadius: 8, padding: "0 8px", fontSize: 12, outline: "none" };
const approveButton = { height: 30, padding: "0 10px", display: "flex", alignItems: "center", gap: 5, border: 0, background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 11, cursor: "pointer", borderRadius: 7 };
const holdButton = { ...approveButton, background: "#0ea5e9" };
const unholdButton = { ...approveButton, background: "#6366f1" };
const rejectButton = { ...approveButton, background: "#dc2626" };
const infoButton = { ...iconButton, background: "#f8fafc" };
const copyButton = { ...approveButton, background: "#475569" };
const actionInfoRow = { display: "flex", alignItems: "center", gap: 6 };
const emptyCell = { padding: "40px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 };
const tableFooter = { padding: "10px 16px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#94a3b8" };
const pager = { display: "flex", alignItems: "center", gap: 12 };

export default AdminWithdraw;
